import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ref, onValue, off, update } from "firebase/database";
import { db } from "../../configs/Firebase";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Button from "../../designSystem/b-molecules/buttons/Button";
import Select from "../../designSystem/b-molecules/formElements/select/Select";
import EditThemeDialog from "./components/EditThemeDialog";
import DateRangePicker from "./components/DateRangePicker";
import {
  getAllRecords,
  filterByDateRange,
  filterByBrand,
  filterBySource,
  filterByCountry,
  filterBySubTheme,
  getDateRangeForPeriod,
  generateDailyTimeSeries,
  generateWeeklyTimeSeries,
  generateMonthlyTimeSeries,
  groupBySubTheme,
  addPercentages,
  prepareSubThemeChartData
} from "./utils/processData";
import { setSelectedPeriod, setCustomDateRange, setSelectedBrand, setSelectedSource, setSelectedCountry, setSelectedSubTheme, setTimeAggregation } from "../../features/customersSlice";
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import style from "./ThemeDetail.module.css";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import { canEditSection, SECTIONS } from "../../utils/permissions";

const ThemeDetail = () => {
  const { themeId } = useParams();
  const navigate = useNavigate();
  const org = useSelector((state) => state.orgStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const customersState = useSelector((state) => state.customers);
  const dispatch = useDispatch();
  const [theme, setTheme] = useState(null);
  const [records, setRecords] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [subThemeChartData, setSubThemeChartData] = useState([]);
  const [allThemes, setAllThemes] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sources, setSources] = useState([]);
  const [subThemes, setSubThemes] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [user, setUser] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [selectedSubThemeBar, setSelectedSubThemeBar] = useState(null);
  const [personas, setPersonas] = useState([]);
  const scrollPositionRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);

  // Check user permissions
  useEffect(() => {
    if (userStatus) {
      setUser(JSON.parse(userStatus.payload));
    }
  }, [userStatus]);

  useEffect(() => {
    if (user && org.payload) {
      setCanEdit(canEditSection(user, org.payload, SECTIONS.CUSTOMERS));
    } else {
      setCanEdit(false);
    }
  }, [user, org.payload]);

  // Load uploads, brands, countries, and themes
  useEffect(() => {
    if (org.payload) {
      // Load uploads
      const uploadsRef = ref(db, `userData/${org.payload}/customers/data`);
      const uploadsUnsubscribe = onValue(uploadsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const uploadsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setUploads(uploadsList);
        } else {
          setUploads([]);
        }
      });

      // Load brands
      const brandsRef = ref(db, `userData/${org.payload}/brands`);
      const brandsUnsubscribe = onValue(brandsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const brandsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setBrands(brandsList);
        } else {
          setBrands([]);
        }
      });

      // Load countries
      const countriesRef = ref(db, `userData/${org.payload}/countries`);
      const countriesUnsubscribe = onValue(countriesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const countriesList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setCountries(countriesList);
        } else {
          setCountries([]);
        }
      });

      // Load sources
      const sourcesRef = ref(db, `userData/${org.payload}/customers/sources`);
      const sourcesUnsubscribe = onValue(sourcesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const sourcesList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setSources(sourcesList);
        } else {
          setSources([]);
        }
      });

      // Load all themes for the edit dialog
      const themesRef = ref(db, `userData/${org.payload}/customers/themes`);
      const themesUnsubscribe = onValue(themesRef, (snapshot) => {
        const themes = snapshot.val();
        if (themes) {
          const themesList = Object.keys(themes).map(id => ({
            id,
            ...themes[id]
          }));
          setAllThemes(themesList);
        }
      });

      // Load personas for displaying persona info on records
      const personasRef = ref(db, `userData/${org.payload}/personas`);
      const personasUnsubscribe = onValue(personasRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const personasList = Object.keys(data)
            .map(id => ({
              id,
              ...data[id]
            }))
            .filter(p => !p.archived);
          setPersonas(personasList);
        } else {
          setPersonas([]);
        }
      });

      // Load sub-themes for the current theme
      if (themeId) {
        const subThemesRef = ref(db, `userData/${org.payload}/customers/themes/${themeId}/subThemes`);
        const subThemesUnsubscribe = onValue(subThemesRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const subThemesList = Object.keys(data).map(id => ({
              id,
              ...data[id]
            }));
            setSubThemes(subThemesList);
          } else {
            setSubThemes([]);
          }
        });

        // Cleanup function to unsubscribe from all listeners
        return () => {
          uploadsUnsubscribe();
          brandsUnsubscribe();
          countriesUnsubscribe();
          sourcesUnsubscribe();
          themesUnsubscribe();
          personasUnsubscribe();
          subThemesUnsubscribe();
        };
      } else {
        // Cleanup function to unsubscribe from all listeners
        return () => {
          uploadsUnsubscribe();
          brandsUnsubscribe();
          countriesUnsubscribe();
          sourcesUnsubscribe();
          themesUnsubscribe();
          personasUnsubscribe();
        };
      }
    }
  }, [org.payload]);

  // Process data with filters
  useEffect(() => {
    if (uploads.length > 0 && themeId) {
      // Get all records
      const allRecords = getAllRecords(uploads);

      // Apply brand filter
      let filteredByBrand = customersState.selectedBrand
        ? filterByBrand(allRecords, customersState.selectedBrand)
        : allRecords;

      // Apply country filter
      let filteredByCountry = customersState.selectedCountry
        ? filterByCountry(filteredByBrand, customersState.selectedCountry)
        : filteredByBrand;

      // Apply source filter
      let filteredBySource = customersState.selectedSource
        ? filterBySource(filteredByCountry, customersState.selectedSource)
        : filteredByCountry;

      // Apply date range filter
      const period = customersState.selectedPeriod || "30";
      const dateRange = getDateRangeForPeriod(period, customersState.customDateRange);

      if (dateRange) {
        const filteredByDate = filterByDateRange(filteredBySource, dateRange.start, dateRange.end);

        // Filter by theme
        let themeRecords = filteredByDate.filter(record => {
          const recordThemeId = record.theme || 'uncategorized';
          return recordThemeId === themeId;
        });

        // Apply sub-theme filter if selected
        if (customersState.selectedSubTheme) {
          themeRecords = filterBySubTheme(themeRecords, customersState.selectedSubTheme);
        }

        // Get theme name
        let themeName = "Uncategorized";
        if (themeRecords.length > 0 && themeRecords[0].themeName) {
          themeName = themeRecords[0].themeName;
        } else {
          // Try to find theme name from themes list
          const themeFromList = allThemes.find(t => t.id === themeId);
          if (themeFromList) {
            themeName = themeFromList.name;
          }
        }

        // Sort by date
        themeRecords.sort((a, b) => {
          try {
            const dateA = a.date ? new Date(a.date).getTime() : 0;
            const dateB = b.date ? new Date(b.date).getTime() : 0;
            if (isNaN(dateA)) return 1;
            if (isNaN(dateB)) return -1;
            return dateB - dateA;
          } catch {
            return 0;
          }
        });

        // Apply sub-theme bar filter if selected
        let filteredRecords = themeRecords;
        if (selectedSubThemeBar) {
          if (selectedSubThemeBar === 'none') {
            filteredRecords = themeRecords.filter(r => !r.subTheme || !r.subThemeName);
          } else {
            filteredRecords = themeRecords.filter(r => r.subTheme === selectedSubThemeBar);
          }
        }

        setRecords(filteredRecords);
        setTheme({ id: themeId, name: themeName, count: themeRecords.length });

        // Generate time series data based on aggregation type (using filtered records)
        if (filteredRecords.length > 0) {
          const validDates = filteredRecords
            .map(r => {
              if (!r.date) return null;
              try {
                const d = new Date(r.date);
                return isNaN(d.getTime()) ? null : d;
              } catch {
                return null;
              }
            })
            .filter(d => d !== null);

          if (validDates.length > 0) {
            const timestamps = validDates.map(d => d.getTime());
            const minDate = new Date(Math.min(...timestamps));
            const maxDate = new Date(Math.max(...timestamps));

            const aggregation = customersState.timeAggregation || "daily";
            let timeSeries;

            if (aggregation === "weekly") {
              timeSeries = generateWeeklyTimeSeries(filteredRecords, minDate, maxDate);
            } else if (aggregation === "monthly") {
              timeSeries = generateMonthlyTimeSeries(filteredRecords, minDate, maxDate);
            } else {
              timeSeries = generateDailyTimeSeries(filteredRecords, minDate, maxDate);
            }

            setTimeSeriesData(timeSeries);
          } else {
            setTimeSeriesData([]);
          }
        } else {
          setTimeSeriesData([]);
        }

        // Generate sub-theme chart data (show all sub-themes regardless of sub-theme filter)
        // Group records by sub-theme
        const subThemeGroups = groupBySubTheme(themeRecords);
        
        // Count records without sub-themes
        const recordsWithoutSubTheme = themeRecords.filter(r => !r.subTheme || !r.subThemeName);
        const noneCount = recordsWithoutSubTheme.length;
        
        // Calculate total records (including those without sub-themes) for percentage calculation
        const totalRecords = themeRecords.length;
        
        // Build chart data array
        let chartDataArray = [];
        
        // Add "None" bar if there are records without sub-themes
        if (noneCount > 0) {
          chartDataArray.push({
            subThemeId: 'none',
            name: 'None',
            count: noneCount,
            percentage: totalRecords > 0 ? ((noneCount / totalRecords) * 100).toFixed(1) : 0,
            records: recordsWithoutSubTheme
          });
        }
        
        // Add sub-theme groups
        if (subThemeGroups.length > 0) {
          const subThemeGroupsWithPercentages = addPercentages(subThemeGroups, totalRecords);
          chartDataArray = [...chartDataArray, ...subThemeGroupsWithPercentages];
        }
        
        // Sort by count (descending) and prepare chart data
        if (chartDataArray.length > 0) {
          chartDataArray.sort((a, b) => b.count - a.count);
          const subThemeChartData = prepareSubThemeChartData(chartDataArray);
          setSubThemeChartData(subThemeChartData);
        } else {
          setSubThemeChartData([]);
        }
      } else {
        setRecords([]);
        setTimeSeriesData([]);
        setSubThemeChartData([]);
      }
    }

    // Restore scroll position after data update if needed
    if (shouldRestoreScrollRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPositionRef.current);
        shouldRestoreScrollRef.current = false;
      });
    }
  }, [uploads, themeId, customersState.selectedBrand, customersState.selectedCountry, customersState.selectedSource, customersState.selectedSubTheme, customersState.selectedPeriod, customersState.customDateRange, customersState.timeAggregation, allThemes, selectedSubThemeBar]);

  function handleEditRecord(record) {
    // Store current scroll position before opening dialog
    // Use window scroll position (most common case)
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    shouldRestoreScrollRef.current = true;
    setSelectedRecord(record);
    setShowEditDialog(true);
  }

  function handleThemeUpdated() {
    // Data will automatically update via Firebase listeners
    // No need to reload the page
    // Scroll will be restored in useEffect after records update
  }

  function handlePeriodChange(e, index) {
    const periods = ["7", "lastWeek", "30", "lastMonth", "90", "custom"];
    dispatch(setSelectedPeriod(periods[index]));
  }

  function handleCustomDateRangeChange(dateRange) {
    dispatch(setCustomDateRange(dateRange));
  }

  function handleClearCustomDateRange() {
    dispatch(setCustomDateRange(null));
    dispatch(setSelectedPeriod("30"));
  }

  function handleTimeAggregationChange(e, index) {
    const aggregations = ["daily", "weekly", "monthly"];
    dispatch(setTimeAggregation(aggregations[index]));
  }

  function handleSubThemeBarClick(subThemeId) {
    // Toggle: if clicking the same bar, deselect it
    if (selectedSubThemeBar === subThemeId) {
      setSelectedSubThemeBar(null);
    } else {
      setSelectedSubThemeBar(subThemeId);
    }
  }

  const periodOptions = [
    { value: "Last 7 days" },
    { value: "Last Week" },
    { value: "Last 30 days" },
    { value: "Last Month" },
    { value: "Last 90 days" },
    { value: "Custom" }
  ];

  const currentPeriodIndex = ["7", "lastWeek", "30", "lastMonth", "90", "custom"].indexOf(customersState.selectedPeriod || "30");
  const isCustomPeriod = customersState.selectedPeriod === "custom";

  const timeAggregationOptions = [
    { value: "Daily" },
    { value: "Weekly" },
    { value: "Monthly" }
  ];

  const currentAggregationIndex = ["daily", "weekly", "monthly"].indexOf(customersState.timeAggregation || "daily");

  const getChartTitle = () => {
    const aggregation = customersState.timeAggregation || "daily";
    if (aggregation === "weekly") return "Weekly Fluctuation";
    if (aggregation === "monthly") return "Monthly Fluctuation";
    return "Daily Fluctuation";
  };

  return (
    <div className="mainContentInner">
      <TitleBar
        title={theme?.name || "Loading..."}
        actions={[
          {
            icon: "arrow_back",
            name: "Back",
            action: () => navigate("/customers"),
            variants: ["text"]
          }
        ]}
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Customers", url: "/customers" }
        ]}
      />

      <div className="contentPadding">
        <div className={style.controls}>
          <div className={style.filters}>
            <div className={style.filterGroup}>
              <Select
                label="Brand"
                type="dropdown"
                options={[{ value: "All Brands" }, ...brands.map(b => ({ value: b.name }))]}
                selectedOptions={[customersState.selectedBrand ? brands.find(b => b.id === customersState.selectedBrand)?.name || "All Brands" : "All Brands"]}
                onChange={(e, index) => {
                  if (index === 0) {
                    dispatch(setSelectedBrand(null));
                  } else {
                    dispatch(setSelectedBrand(brands[index - 1].id));
                  }
                }}
                multiselect={false}
              />
            </div>
            <div className={style.filterGroup}>
              <Select
                label="Country"
                type="dropdown"
                options={[{ value: "All Countries" }, ...countries.map(c => ({ value: c.name }))]}
                selectedOptions={[customersState.selectedCountry ? countries.find(c => c.id === customersState.selectedCountry)?.name || "All Countries" : "All Countries"]}
                onChange={(e, index) => {
                  if (index === 0) {
                    dispatch(setSelectedCountry(null));
                  } else {
                    dispatch(setSelectedCountry(countries[index - 1].id));
                  }
                }}
                multiselect={false}
              />
            </div>
            <div className={style.filterGroup}>
              {(() => {
                const fallbackSourceOptions = [
                  { label: "All Sources", key: null },
                  { label: "Customer Service", key: "customer_service" },
                  { label: "Google Play", key: "google_play" },
                  { label: "App Store", key: "app_store" },
                  { label: "Other", key: "other" }
                ];

                const dynamicSourceOptions = sources.length > 0
                  ? [{ label: "All Sources", key: null }, ...sources.map(s => ({ label: s.name, key: s.key }))]
                  : fallbackSourceOptions;

                const currentSourceOption =
                  dynamicSourceOptions.find(opt => opt.key === customersState.selectedSource) ||
                  dynamicSourceOptions[0];

                return (
                  <Select
                    label="Feedback Type"
                    type="dropdown"
                    options={dynamicSourceOptions.map(opt => ({ value: opt.label }))}
                    selectedOptions={[currentSourceOption.label]}
                    onChange={(e, index) => {
                      const selected = dynamicSourceOptions[index];
                      if (!selected || !selected.key) {
                        dispatch(setSelectedSource(null));
                      } else {
                        dispatch(setSelectedSource(selected.key));
                      }
                    }}
                    multiselect={false}
                  />
                );
              })()}
            </div>
            {subThemes.length > 0 && (
              <div className={style.filterGroup}>
                <Select
                  label="Sub-theme"
                  type="dropdown"
                  options={[{ value: "All Sub-themes" }, ...subThemes.map(st => ({ value: st.name }))]}
                  selectedOptions={[
                    customersState.selectedSubTheme
                      ? subThemes.find(st => st.id === customersState.selectedSubTheme)?.name || "All Sub-themes"
                      : "All Sub-themes"
                  ]}
                  onChange={(e, index) => {
                    if (index === 0) {
                      dispatch(setSelectedSubTheme(null));
                    } else {
                      dispatch(setSelectedSubTheme(subThemes[index - 1].id));
                    }
                  }}
                  multiselect={false}
                />
              </div>
            )}
            <div className={style.filterGroup}>
              <Select
                label="Period"
                type="dropdown"
                options={periodOptions}
                selectedOptions={[periodOptions[currentPeriodIndex]?.value || "Last 30 days"]}
                onChange={handlePeriodChange}
                multiselect={false}
              />
            </div>

          </div><div className={style.chartTypeToggle}><div className={style.filterGroup}>
            <Select
              label="View"
              type="dropdown"
              options={timeAggregationOptions}
              selectedOptions={[timeAggregationOptions[currentAggregationIndex]?.value || "Daily"]}
              onChange={handleTimeAggregationChange}
              multiselect={false}
            />
          </div></div>
        </div>
        {isCustomPeriod && (
          <div className={style.customDateRange}>
            <DateRangePicker
              startDate={customersState.customDateRange?.start || null}
              endDate={customersState.customDateRange?.end || null}
              onDateChange={handleCustomDateRangeChange}
              onClear={handleClearCustomDateRange}
            />
          </div>
        )}

        {timeSeriesData.length > 0 && (
          <div className={style.chartContainer}>
            <h3>{getChartTitle()}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="displayDate"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#4ea448"
                  strokeWidth={2}
                  name="Feedback Count"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {subThemes.length > 0 && subThemeChartData.length > 0 && (
          <div className={style.chartContainer}>
            <h3>Sub-theme Distribution</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={subThemeChartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} />
                <Tooltip 
                  formatter={(value, name) => [value, 'Count']}
                  labelFormatter={(label) => `Sub-theme: ${label}`}
                />
                <Bar 
                  dataKey="value"
                  onClick={(data, index, e) => {
                    if (data && data.subThemeId) {
                      handleSubThemeBarClick(data.subThemeId);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {subThemeChartData.map((entry, index) => {
                    // Determine fill color based on selection state
                    let fillColor = entry.fill;
                    if (selectedSubThemeBar !== null) {
                      // If a bar is selected, grey out non-selected bars
                      if (entry.subThemeId !== selectedSubThemeBar) {
                        fillColor = 'var(--on--600)';
                      }
                    }
                    return (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={fillColor}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {subThemes.length > 0 && subThemeChartData.length === 0 && (
          <div className={style.chartContainer}>
            <h3>Sub-theme Distribution</h3>
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--on--300)' }}>
              No sub-theme data available for the selected filters
            </div>
          </div>
        )}

        <div className={style.recordsList}>
          <h3>Feedback Items ({records.length})</h3>
          <div className={style.records}>
            {records.map((record, index) => (
              <div key={record.id || index} className={style.recordItem}>
                <div className={style.recordContent}>
                  <q className={style.recordText}>{record.feedbackText}</q>
                  <div className={style.recordMeta}>
                    {record.date && (
                      <span className={style.recordDate}>
                        <Icon>calendar_today</Icon> {new Date(record.date).toLocaleDateString()}
                      </span>
                    )}
                    {record.source && (
                      <span className={style.recordSource}>
                        <Icon>source</Icon> {record.source.replace('_', ' ')}
                      </span>
                    )}
                    {record.subThemeName && (
                      <span className={style.recordSubTheme}>
                        <Icon>category</Icon> {record.subThemeName}
                      </span>
                    )}
                    {record.sentiment && (
                      <span className={`${style.recordSentiment} ${style[record.sentiment]}`}>
                        <Icon variants={["inheritColor"]}>
                          {record.sentiment === 'positive' ? 'sentiment_satisfied' :
                            record.sentiment === 'negative' ? 'sentiment_dissatisfied' :
                              'sentiment_neutral'}
                        </Icon> 
                      </span>
                    )}
                    {record.personaId && (
                      <span className={style.recordPersona}>
                        <Icon>person</Icon> {personas.find(p => p.id === record.personaId)?.name || 'Persona'}
                      </span>
                    )}
                    {canEdit && (
                  <div className={style.recordActions}>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => handleEditRecord(record)}
                    >
                      <Icon>edit</Icon>
                    </Button>
                  </div>
                )}
                  </div>
                </div>
                
              </div>
            ))}
          </div>
        </div>
      </div>

      <EditThemeDialog
        show={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        themes={allThemes}
        orgId={org.payload}
        uploadId={selectedRecord?.uploadId}
        onThemeUpdated={handleThemeUpdated}
      />
    </div>
  );
};

export default ThemeDetail;

